import React from 'react';
import { motion } from 'framer-motion';
import LatestBadge from './LatestBadge';
import { getSortedGalleryItems } from '../../assets/data/gallery';

const ImageGrid = ({ images, categoryColor, onImageClick }) => {
  // Sort images with latest first
  const sortedImages = getSortedGalleryItems(images);

  const renderMediaContent = (item) => {
    if (item.type === 'video') {
      return (
        <div style={styles.videoContainer}>
          <img 
            src={item.thumbnail} 
            alt={item.alt} 
            loading="lazy" 
            style={styles.image} 
          />
          <div style={styles.videoLabel}>
            <i className="fas fa-video" style={styles.videoIcon}></i>
            <span>Video</span>
          </div>
          <div style={styles.playButton}>
            <i className="fas fa-play" style={styles.playIcon}></i>
          </div>
        </div>
      );
    } else {
      return (
        <img 
          src={item.src} 
          alt={item.alt} 
          loading="lazy" 
          style={styles.image} 
        />
      );
    }
  };

  const getOverlayIcon = (item) => {
    if (item.type === 'video') {
      return 'fas fa-play';
    }
    return 'fa fa-search-plus';
  };

  return (
    <motion.div 
      style={styles.grid}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {sortedImages.map((item, index) => (
        <motion.div 
          key={item.src || item.id}
          className="image-grid-card"
          style={styles.card}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: index * 0.1 }}
          whileHover={{ 
            scale: 1.03, 
            boxShadow: `0 10px 25px rgba(0, 0, 0, 0.2), 0 0 0 2px ${categoryColor}` 
          }}
          onClick={() => onImageClick(item)}
        >
          <div style={styles.wrapper}>
            {/* Show latest badge if this is the latest installation */}
            {item.isLatest && <LatestBadge />}
            {renderMediaContent(item)}
            <div className="overlay" style={styles.overlay}>
              <span 
                className="viewIcon"
                style={{
                  ...styles.viewIcon,
                  backgroundColor: item.type === 'video' ? 'rgba(220, 53, 69, 0.9)' : 'rgba(255, 255, 255, 0.9)'
                }}
              >
                <i className={getOverlayIcon(item)}></i>
              </span>
            </div>
          </div>
          {/* <div style={styles.caption}>
            <p style={styles.captionText}>{item.caption}</p>
          </div> */}
        </motion.div>
      ))}
    </motion.div>
  );
};

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '25px',
    marginBottom: '50px',
  },
  card: {
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'white',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  wrapper: {
    position: 'relative',
    overflow: 'hidden',
    paddingTop: '75%', // 4:3 Aspect Ratio
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  videoContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  videoLabel: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    zIndex: 2,
  },
  videoIcon: {
    fontSize: '0.7rem',
  },
  playButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: 'rgba(220, 53, 69, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  playIcon: {
    fontSize: '18px',
    color: 'white',
    marginLeft: '2px', // Slight offset for visual centering
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  viewIcon: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0078ff',
    fontSize: '1.2rem',
    transform: 'translateY(20px)',
    transition: 'transform 0.3s ease',
  },
  caption: {
    padding: '15px',
  },
  captionText: {
    margin: 0,
    color: '#555',
    fontSize: '0.95rem',
  }
};

// Add hover effects via CSS-in-JS
const hoverStyles = `
  .image-grid-card:hover .overlay {
    opacity: 1;
  }
  .image-grid-card:hover .viewIcon {
    transform: translateY(0);
  }
  .image-grid-card:hover img {
    transform: scale(1.05);
  }
`;

// Inject styles only once
if (typeof document !== 'undefined' && !document.getElementById('image-grid-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'image-grid-styles';
  styleSheet.type = 'text/css';
  styleSheet.innerText = hoverStyles;
  document.head.appendChild(styleSheet);
}

export default ImageGrid;